import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as authService from '../services/authService';
import * as creditService from '../services/creditService';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null);
	const [token, setToken] = useState(() => localStorage.getItem('token') || '');
	const [loading, setLoading] = useState(false);
	const [credits, setCredits] = useState(0);

	const login = useCallback(async (email, password) => {
		setLoading(true);
		try {
			const res = await authService.login(email, password);
			localStorage.setItem('token', res.token);
			setToken(res.token);
			setUser(res.user);
			toast.success('Logged in');
			return true;
		} catch (err) {
			toast.error(err?.response?.data?.message || 'Login failed');
			return false;
		} finally {
			setLoading(false);
		}
	}, []);

	const register = useCallback(async (data) => {
		setLoading(true);
		try {
			await authService.register(data);
			toast.success('Registration successful. Check your email for verification.');
			return true;
		} catch (err) {
			toast.error(err?.response?.data?.message || 'Registration failed');
			return false;
		} finally {
			setLoading(false);
		}
	}, []);

	const logout = useCallback(() => {
		localStorage.removeItem('token');
		setToken('');
		setUser(null);
		setCredits(0);
		toast.info('Logged out');
	}, []);

	const updateCredits = useCallback(async () => {
		if (!token) return;
		try {
			const res = await creditService.getBalance();
			setCredits(res.credits ?? 0);
		} catch {
			// ignore
		}
	}, [token]);

	const updateUser = useCallback(async () => {
		if (!token) return;
		try {
			const res = await authService.getProfile();
			setUser(res.user);
		} catch {
			// ignore
		}
	}, [token]);

	useEffect(() => {
		const load = async () => {
			if (!token) return;
			setLoading(true);
			try {
				const res = await authService.getProfile();
				setUser(res.user);
				await updateCredits();
			} catch {
				// token invalid -> logout
				localStorage.removeItem('token');
				setToken('');
			} finally {
				setLoading(false);
			}
		};
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const value = useMemo(
		() => ({ user, token, loading, credits, login, register, logout, updateCredits, updateUser }),
		[user, token, loading, credits, login, register, logout, updateCredits, updateUser]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


